import React from 'react';
import { IonButton, IonButtons, IonIcon, IonImg, IonMenuToggle } from '@ionic/react';
import { ChallengeCountdownState } from 'hooks/useChallengeCountdown';
import { ellipsisVertical } from 'ionicons/icons';

interface Props {
  title: string;
  showBack?: boolean;
  onBack?: () => void;
  showBadge?: boolean;
  countdown?: string;
  countdownState?: ChallengeCountdownState;
  showTimer?: boolean;
  badgeIcon?: 'challenge' | 'crown' | string;
  className?: string;
  activeDotPage?: number;
  onDotClick?: (page: number) => void;
  onSwipe?: (direction: 'left' | 'right') => void;
}

const LeaderboardTopLayer: React.FC<Props> = ({
  title,
  showBack = true,
  onBack,
  showBadge = true,
  showTimer = false,
  countdown,
  countdownState,
  badgeIcon,
  className,
  activeDotPage,
  onDotClick,
  onSwipe,
}) => {
  const touchStartX = React.useRef<number | null>(null);
  const touchStartY = React.useRef<number | null>(null);
  const shouldIgnoreSwipe = React.useRef(false);

  const handleTouchStart = (e: React.TouchEvent) => {
    if (e.touches.length !== 1) {
      shouldIgnoreSwipe.current = true;
      touchStartX.current = null;
      touchStartY.current = null;
      return;
    }

    const target = e.target as HTMLElement | null;
    shouldIgnoreSwipe.current = Boolean(
      target?.closest('.leaderboard-hero__back, .leaderboard-pager__dot')
    );

    touchStartX.current = e.touches[0].clientX;
    touchStartY.current = e.touches[0].clientY;
  };

  const resetTouch = () => {
    touchStartX.current = null;
    touchStartY.current = null;
    shouldIgnoreSwipe.current = false;
  };

  const handleTouchEnd = (e: React.TouchEvent) => {
    if (shouldIgnoreSwipe.current || touchStartX.current === null || touchStartY.current === null) {
      resetTouch();
      return;
    }

    const dx = e.changedTouches[0].clientX - touchStartX.current;
    const dy = e.changedTouches[0].clientY - touchStartY.current;
    resetTouch();

    const isHorizontalSwipe = Math.abs(dx) > Math.abs(dy) * 1.2;
    if (!isHorizontalSwipe || Math.abs(dx) < 40) return;
    if (onSwipe) onSwipe(dx < 0 ? 'left' : 'right');
  };

  const handleBack = () => {
    if (onBack) onBack();
    else window.history.back();
  };

  const getBadgeSrc = (badge?: string) => {
    if (!badge) return undefined;
    if (badge === 'challenge') return 'assets/images/leaderboard-challenge.png';
    if (badge === 'crown') return 'assets/images/leaderboard-crown.png';
    return badge;
  };

  const badgeClass =
    badgeIcon === 'challenge'
      ? 'leaderboard-hero__badge-icon leaderboard-hero__badge-icon--challenge'
      : 'leaderboard-hero__badge-icon';

  const showDots = activeDotPage !== undefined && onDotClick !== undefined;
  const isDarkMode = false;
  const iconDarMode = isDarkMode ? { color: '#000' } : { color: '#fff' };

  return (
    <div
      className={`leaderboard-top-layer ${className ?? ''}`}
      onTouchStart={onSwipe ? handleTouchStart : undefined}
      onTouchEnd={onSwipe ? handleTouchEnd : undefined}
      onTouchCancel={onSwipe ? resetTouch : undefined}
    >
      <div className="leaderboard-hero leaderboard-hero--group">
        {showBack && (
          <button className="leaderboard-hero__back" aria-label="Back" onClick={handleBack}>
            <IonImg src="assets/images/back-round.png" />
          </button>
        )}

        {showTimer && countdown && (
          <div className="events-hero__tabs events-timer__tabs">
            {countdownState != 'none' && (
              <>
                <span className="events-hero__tab events-hero__tab--active">
                  {countdownState === 'upcoming' ? 'Begins' : 'Ends in'}
                </span>
                <span className="events-hero__timer">{countdown}</span>
              </>
            )}
          </div>
        )}
        {!showTimer && showBadge && (
          <div className="leaderboard-hero__card">
            <div className="leaderboard-hero__title">{title}</div>
            {showBadge &&
              (getBadgeSrc(badgeIcon) ? (
                <div className={badgeClass} aria-hidden="true">
                  <img src={getBadgeSrc(badgeIcon)} />
                </div>
              ) : (
                <div className="leaderboard-hero__badge" aria-hidden="true" />
              ))}
          </div>
        )}

        <IonMenuToggle>
          <IonButton className={'no-menu-circle'}>
            <IonIcon icon={ellipsisVertical} className="menu-icon" style={iconDarMode} />
          </IonButton>
        </IonMenuToggle>
      </div>

      {showDots && (
        <div className="leaderboard-pager" role="tablist" aria-label="Switch view">
          {[0, 1].map((i) => (
            <button
              key={i}
              role="tab"
              aria-selected={activeDotPage === i}
              aria-label={i === 0 ? 'Leaderboard' : 'Challenges'}
              className={`leaderboard-pager__dot${
                activeDotPage === i ? ' leaderboard-pager__dot--active' : ''
              }`}
              onClick={() => onDotClick(i)}
            />
          ))}
        </div>
      )}
    </div>
  );
};

export default LeaderboardTopLayer;
